import type React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ExpandedLayout } from '../../config/constants';
import type { ConversationPair } from '../../services/chatStorage';
import ChatMessages from '../ChatMessages';

interface ExpandedChatContainerProps {
    conversationPairs: ConversationPair[];
    streamingMessage?: {
        id: string;
        content: string;
        isComplete: boolean;
        isThinking: boolean;
        suggestedFollowUps?: string[];
    };
    isLoading: boolean;
    primaryColor: string;
    siteConfig: { name: string };
    messagesEndRef: React.RefObject<HTMLDivElement>;
    evaluationEnabled?: boolean;
    domain: string;
    onLatestQuestionRef?: (ref: HTMLElement | null) => void;
    onFollowUpClick?: (question: string) => void;
    onSuggestedAnswerClick?: (answer: string) => void;
    onRetry?: () => void;
    hideInlineCTAs?: boolean;
    viewLayout?: ExpandedLayout; // Active layout for positioning/sizing
    // Inline demo CTA props (shown when 👇 emoji detected in last message)
    showInlineDemoCTA?: boolean;
    isCtaPage?: boolean;
    inlineCtaOptionEnabled?: boolean;
    visitorEmail?: string | null;
    onBookingFlowStart?: (
        ctaId: string,
        redirectUrl: string,
        bookingMessage: string,
        ctaAction?: 'navigate' | 'popup' | 'in-chat'
    ) => void;
    // In-chat calendar embed rendered at the bottom of messages
    calendarEmbed?: React.ReactNode;
    trailingContentHeight?: number;
    // Height of the overlaid input shell in expanded mode.
    // We use this to reserve matching scroll space so messages can travel
    // underneath the glass shell instead of ending against the panel behind it.
    bottomInset?: number;
    viewportResizeKey?: string;
    hideInlineProfilingProgress?: boolean;
    /** Whether post-conversion qualification mode is active (enables funnel tracking). */
    isPostConversion?: boolean;
}

const ExpandedChatContainer: React.FC<ExpandedChatContainerProps> = ({
    conversationPairs,
    streamingMessage,
    isLoading,
    primaryColor,
    siteConfig,
    messagesEndRef,
    evaluationEnabled = true,
    domain,
    onLatestQuestionRef,
    onFollowUpClick,
    onSuggestedAnswerClick,
    onRetry,
    hideInlineCTAs,
    viewLayout = 'expanded',
    showInlineDemoCTA,
    isCtaPage,
    inlineCtaOptionEnabled,
    visitorEmail,
    onBookingFlowStart,
    calendarEmbed,
    trailingContentHeight,
    bottomInset = 0,
    viewportResizeKey,
    hideInlineProfilingProgress,
    isPostConversion = false,
}) => {
    const isCopilotLayout = viewLayout === 'copilot';
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [embedSpacerHeight, setEmbedSpacerHeight] = useState(0);

    // When the calendar embed is shown, add a spacer so it can scroll to the top,
    // then auto-scroll down so the calendar is visible.
    // When the embed closes, clear the spacer and scroll to the last message
    // (same as normal new-message behavior).
    useLayoutEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        if (calendarEmbed) {
            const padding = 16;
            const usableViewportHeight = Math.max(0, container.clientHeight - bottomInset);
            setEmbedSpacerHeight(
                Math.max(0, usableViewportHeight - (trailingContentHeight ?? 0) - padding)
            );
            requestAnimationFrame(() => {
                container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
            });
        } else {
            setEmbedSpacerHeight(0);
        }
    }, [calendarEmbed, trailingContentHeight, bottomInset]);

    // After the embed closes, the post-booking response arrives async.
    // Scroll to it once loading finishes (response fully rendered).
    const needsPostBookingScroll = useRef(false);

    useEffect(() => {
        if (calendarEmbed) {
            needsPostBookingScroll.current = true;
        } else if (needsPostBookingScroll.current && !isLoading) {
            needsPostBookingScroll.current = false;
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [calendarEmbed, isLoading, messagesEndRef]);

    return (
        <>
            {/* Mobile responsive styles for container */}
            <style>{`
                .chat-messages-container {
                    transition: padding 0.3s ease !important;
                    scrollbar-width: thin;
                    scrollbar-color: transparent transparent;
                }
                .chat-messages-container:hover {
                    scrollbar-color: rgba(0,0,0,0.2) transparent;
                }
                .chat-messages-container::-webkit-scrollbar {
                    width: 6px;
                }
                .chat-messages-container::-webkit-scrollbar-track {
                    background: transparent;
                }
                .chat-messages-container::-webkit-scrollbar-thumb {
                    background: transparent;
                    border-radius: 3px;
                }
                .chat-messages-container:hover::-webkit-scrollbar-thumb {
                    background: rgba(0,0,0,0.2);
                }
                @media (max-width: 1235px) {
                    .chat-messages-container:not(.chat-messages-container-copilot) {
                        padding-top: 20px !important;
                        padding-right: 20px !important;
                        padding-left: 20px !important;
                    }
                }
            `}</style>
            <div
                ref={scrollContainerRef}
                className={`chat-messages-container ${isCopilotLayout ? 'chat-messages-container-copilot' : ''}`}
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    // Reserve scrollbar space on BOTH edges to keep content centered (default mode only)
                    // This prevents layout shift when scrollbar appears/disappears
                    // Copilot layout is narrower (440px), so we skip this to preserve space
                    ...(isCopilotLayout ? {} : { scrollbarGutter: 'stable both-edges' }),
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: conversationPairs.length === 0 ? 'center' : 'flex-start',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                    gap: '20px',
                    padding: isCopilotLayout ? '20px 20px' : '20px 40px',
                    // In expanded mode the input shell is absolutely overlaid at the bottom.
                    // Reserve the same amount of space inside the scroller so the last
                    // messages can scroll behind that shell cleanly.
                    paddingBottom: bottomInset > 0 ? `${bottomInset}px` : '0px',
                    // Keep programmatic scrolling aligned with the same reserved space.
                    scrollPaddingBottom: bottomInset > 0 ? `${bottomInset}px` : '0px',
                    scrollBehavior: 'smooth',
                    minHeight: 0, // Ensure proper flex shrinking
                }}
            >
                <ChatMessages
                    conversationPairs={conversationPairs}
                    isLoading={isLoading}
                    primaryColor={primaryColor}
                    siteConfig={siteConfig}
                    messagesEndRef={messagesEndRef}
                    evaluationEnabled={evaluationEnabled}
                    domain={domain}
                    onLatestQuestionRef={onLatestQuestionRef}
                    streamingMessage={streamingMessage}
                    onFollowUpClick={onFollowUpClick}
                    onSuggestedAnswerClick={onSuggestedAnswerClick}
                    onRetry={onRetry}
                    hideInlineCTAs={hideInlineCTAs}
                    viewLayout={viewLayout}
                    showInlineDemoCTA={showInlineDemoCTA}
                    isCtaPage={isCtaPage}
                    inlineCtaOptionEnabled={inlineCtaOptionEnabled}
                    visitorEmail={visitorEmail}
                    onBookingFlowStart={onBookingFlowStart}
                    trailingContentHeight={trailingContentHeight}
                    bottomInset={bottomInset}
                    viewportResizeKey={viewportResizeKey}
                    hideInlineProfilingProgress={hideInlineProfilingProgress}
                    isPostConversion={isPostConversion}
                />
                {calendarEmbed}
                {calendarEmbed && embedSpacerHeight > 0 && (
                    <div style={{ minHeight: embedSpacerHeight, flexShrink: 0 }} />
                )}
            </div>
        </>
    );
};

export default ExpandedChatContainer;
